import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import { equipAvatarFrame, getEquippedFrame, getStore, purchaseStoreItem } from '@/lib/data/provider';

/** The store catalogue + the caller's owned/equipped state + coin balance. */
export function useStore() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['store', userId ?? 'anon'],
    queryFn: getStore,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

export function usePurchaseItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemKey: string) => purchaseStoreItem(itemKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store'] });
      queryClient.invalidateQueries({ queryKey: ['rewards', 'wallet'] });
    },
  });
}

export function useEquipFrame() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemKey: string | null) => equipAvatarFrame(itemKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store'] });
      queryClient.invalidateQueries({ queryKey: ['equipped-frame'] });
    },
  });
}

/** The caller's equipped avatar-frame key (for rendering on their own avatar). */
export function useEquippedFrame() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['equipped-frame', userId ?? 'anon'],
    queryFn: getEquippedFrame,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}
